import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Col, Row } from 'react-bootstrap'
import { Field, reduxForm } from 'redux-form'
import { get, map, keys, includes } from 'lodash'

import { Input } from 'Components/Form/Input'
import { Dropzone } from 'Components/Form/Dropzone'
import SubmitButton from 'Components/Form/SubmitButton'
import { SectionLabel } from 'Components/Section/SectionLabel'
import { SectionTitle } from 'Components/Section/SectionTitle'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { SectionWrapper } from 'Components/Section/SectionWrapper'
import { SubmitButtonBlock } from 'Components/Form/SubmitButton/SubmitButtonBlock'

import { required, zeroRequired, email } from 'Utils/Validators/formValidation'

import { getUserById } from 'Reducers/Users/actions'
import { getCities } from 'Reducers/City/actions'
import { getClients } from 'Reducers/Clients/actions'
import { USER_STATUSES } from 'Reducers/Users/const'
import { authorization } from 'Module/Auth/Auth'
import { CONFIG } from 'Config/config'
import { isClientRole } from 'Utils/Validators/roles'
import { roleOptions } from 'Containers/UserManagement/UserDetails/utils'
import { Checkbox } from 'Components/Form/Checkbox'

const statusOptions = [
  {
    label: 'Active',
    value: USER_STATUSES.active,
  },
  {
    label: 'Blocked',
    value: USER_STATUSES.blocked,
  },
]

const setRoles = availableRoles =>
  map(roleOptions, (data, roleName) => ({
    ...data,
    disabled: !includes(availableRoles, roleName),
  }))

const roleOptionsByUserRole = {
  [CONFIG.USER_ROLE.ADMIN]: setRoles(keys(roleOptions)),
  [CONFIG.USER_ROLE.CITY_MANAGER]: setRoles([
    CONFIG.USER_ROLE.CITY_MANAGER,
    CONFIG.USER_ROLE.CLIENT,
  ]),
  [CONFIG.USER_ROLE.CLIENT_ADMIN]: setRoles([
    CONFIG.USER_ROLE.CLIENT_ADMIN,
    CONFIG.USER_ROLE.CLIENT,
  ]),
  [CONFIG.USER_ROLE.CLIENT]: setRoles([CONFIG.USER_ROLE.CLIENT]),
}

class UserManagementDetailsEditForm extends PureComponent {
  componentDidMount = (
    { getUserById, getCities, getClients, userId } = this.props,
  ) => {
    getUserById(userId)
    getCities()
    getClients()
  }

  render = ({ handleSubmit, cities, clients, profilePicture } = this.props) => (
    <form>
      <SectionWrapper className="section-wrapper first">
        <SectionTitle className="section-title">User Details</SectionTitle>
        <Col xs={12}>
          <Col xs={10}>
            <Row>
              <Col xs={3}>
                <SectionLabel className="section-label">
                  First Name
                </SectionLabel>
                <Field
                  component={Input}
                  id="firstName"
                  name="firstName"
                  validate={[required]}
                />
              </Col>
              <Col xs={3}>
                <SectionLabel className="section-label">Last Name</SectionLabel>
                <Field
                  component={Input}
                  id="lastName"
                  name="lastName"
                  validate={[required]}
                />
              </Col>
              <Col xs={3}>
                <SectionLabel className="section-label">
                  Mobile Phone
                </SectionLabel>
                <Field
                  component={Input}
                  id="mobilePhone"
                  name="mobilePhone"
                  validate={[required]}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <SectionLabel className="section-label">
                  Email Address
                </SectionLabel>
                <Field
                  component={Input}
                  id="email"
                  name="email"
                  validate={[required, email]}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={3}>
                <SectionLabel className="section-label">Role</SectionLabel>
                <Field
                  component={SelectDashboard}
                  name="role"
                  props={{
                    options:
                      roleOptionsByUserRole[authorization.getUser().role],
                    sorted: true,
                  }}
                  validate={[required]}
                />
              </Col>
              {isClientRole(this.props.activeRole) ? (
                <Col xs={3}>
                  <SectionLabel className="section-label">Client</SectionLabel>
                  <Field
                    component={SelectDashboard}
                    id="client"
                    name="clientId"
                    props={{
                      options: clients,
                      sorted: true,
                    }}
                    validate={[required]}
                  />
                </Col>
              ) : (
                <Col xs={3}>
                  <SectionLabel className="section-label">City</SectionLabel>
                  <Field
                    component={SelectDashboard}
                    name="cityId"
                    props={{
                      options: cities,
                      sorted: true,
                    }}
                    validate={[required]}
                  />
                </Col>
              )}
              <Col xs={3}>
                <SectionLabel className="section-label">Status</SectionLabel>
                <Field
                  component={SelectDashboard}
                  name="status"
                  props={{
                    options: statusOptions,
                    sorted: true,
                  }}
                  validate={[zeroRequired]}
                />
              </Col>
            </Row>
            {this.props.activeRole === CONFIG.USER_ROLE.CITY_MANAGER ? (
              <Row>
                <Col xs={3}>
                  <SectionLabel className="section-label">
                    Is Assistant
                  </SectionLabel>
                  <Field component={Checkbox} name="isCityManagerAssistant" />
                </Col>
              </Row>
            ) : null}
          </Col>
          <Col xs={2}>
            <Field
              component={Dropzone}
              id="profilePicture"
              name="profilePicture"
              src={profilePicture}
            />
          </Col>
        </Col>
      </SectionWrapper>
      <SubmitButtonBlock>
        <SubmitButton value="Save" onClick={handleSubmit} />
      </SubmitButtonBlock>
    </form>
  )
}

UserManagementDetailsEditForm.propTypes = {
  activeRole: PropTypes.string,
  userById: PropTypes.object.isRequired,
  cities: PropTypes.array.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  getUserById: PropTypes.func.isRequired,
  getCities: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  initialValues: {
    firstName: state.users.userById.firstName,
    lastName: state.users.userById.lastName,
    mobilePhone: state.users.userById.mobilePhone,
    email: state.users.userById.email,
    businessNumber: get(state.users.userById, 'paymentDetails.businessNumber'),
    clientId: get(state.users.userById, 'client.id')
      ? String(get(state.users.userById, 'client.id'))
      : null,
    role: state.users.userById.role,
    cityId: get(state.users.userById, 'city.id')
      ? String(get(state.users.userById, 'city.id'))
      : null,
    status: USER_STATUSES[state.users.userById.status],
    profilePicture: state.users.userById.profilePicture,
    isCityManagerAssistant:
      state.users.userById.subrole === CONFIG.SUBROLES.ASSISTANT,
  },
  activeRole: get(state, 'form[user-details-edit.values].role'),
  profilePicture: state.users.userById.profilePicture,
  userById: state.users.userById,
  cities: state.city.citiesList.map(c => ({ value: c.id, label: c.name })),
  clients: state.clients.clientsList.map(c => ({ value: c.id, label: c.name })),
})

UserManagementDetailsEditForm = reduxForm({
  form: 'user-details-edit',
  enableReinitialize: true,
})(UserManagementDetailsEditForm)

UserManagementDetailsEditForm = connect(
  mapStateToProps,
  {
    getUserById,
    getCities,
    getClients,
  },
)(UserManagementDetailsEditForm)

export { UserManagementDetailsEditForm }
